import { ComponentChildren, JSX } from 'preact'
import { useRef, useState } from 'preact/hooks'
import classnames from 'classnames'
import { ActionType, BlockType, TBlockProps } from '@gsc/types'
import genUID from '../../../shared/uid'
import { previewProps } from '../../preview'
import { createEvent, shouldIgnoreEvents } from '../../helpers/events'
import { useIsMounted } from '../../hooks'
import { Banner } from '../Notifications/Banner'
import { useActions } from '../useActions'
import { useComponentStyle } from '../Style'
import { useForm } from './useForm'
import { FormSuccess } from './FormSuccess'
import FormProvider from './FormProvider'

interface Props extends TBlockProps<BlockType.Group> {
  children: ComponentChildren
}

export function Form({ children, actions = [], ...props }: Props): JSX.Element {
  const { current: origin } = useRef(genUID())
  const formRef = useRef<HTMLFormElement>()
  const processActions = useActions()
  const methods = useForm()
  const isMounted = useIsMounted()
  const [flags, setFlags] = useState({
    submitting: false,
    submitted: false,
  })
  const { valid = true, pristine = true } = methods.getFormFlags()
  const { submitting, submitted } = flags

  const { classes } = useComponentStyle('container', props)
  const className = classnames(classes, {
    valid,
    invalid: !valid,
    pristine,
    dirty: !pristine,
    submit: !submitting && submitted,
    submitting,
    clickable: actions.length > 0,
  })

  const handleSubmit = (e: Event) => {
    const form: HTMLFormElement = e.target as HTMLFormElement

    setFlags({ submitting: true, submitted: false })

    methods.handleSubmit(() => {
      if (!isMounted()) {
        return
      }

      ;(() => form.offsetHeight)() // force reflow to re-enable animation

      setFlags({ submitting: false, submitted: true })
    })(e)
  }

  const handleClick = async (e: MouseEvent) => {
    e.stopPropagation()

    if (!actions.length || shouldIgnoreEvents(e.target as HTMLElement)) {
      return
    }

    await processActions(actions, origin)

    const shouldSubmit = actions.some(a => a.type === ActionType.Submit)
    if (shouldSubmit) {
      formRef.current.dispatchEvent(createEvent('submit'))
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        noValidate
        onSubmit={handleSubmit}
        onClick={handleClick}
        className={className}
        {...(PREVIEW && { autocomplete: 'off' })}
        {...previewProps(BlockType.Group, props.id)}
      >
        {children}
        <Banner name={origin} />
        <FormSuccess />
      </form>
    </FormProvider>
  )
}
